import { ArrowRight } from 'lucide-react';
import type { StatusMapping } from './types';

interface StatusGroupConfigProps {
  /** Raw SFDC statuses available for this campaign */
  sfdcStatuses: string[];
  /** Scorecard column names (e.g. ["Attended", "No-Show"]) */
  scorecardColumns: string[];
  /** Current mapping: column → sfdc statuses */
  mapping: StatusMapping;
  onChange: (mapping: StatusMapping) => void;
  disabled?: boolean;
}

const CM_STATUS_COLORS: Record<string, string> = {
  Attended:         'bg-matcha-50 text-matcha-700 dark:bg-matcha-950/50 dark:text-matcha-400',
  'No-show':        'bg-pomegranate-50 text-pomegranate-700 dark:bg-pomegranate-950/50 dark:text-pomegranate-400',
  Registered:       'bg-blueberry-50 text-blueberry-700 dark:bg-blueberry-950/50 dark:text-blueberry-400',
  Sent:             'bg-nightshade-100 text-nightshade-600 dark:bg-nightshade-800 dark:text-nightshade-300',
  Declined:         'bg-pomegranate-50 text-pomegranate-600 dark:bg-pomegranate-950/50 dark:text-pomegranate-400',
  'Pending Approval': 'bg-lemon-50 text-lemon-700 dark:bg-lemon-950/50 dark:text-lemon-400',
  Responded:        'bg-matcha-50 text-matcha-600 dark:bg-matcha-950/50 dark:text-matcha-400',
};

function statusChipClass(status: string) {
  return CM_STATUS_COLORS[status] ?? 'bg-nightshade-100 text-nightshade-600 dark:bg-nightshade-800 dark:text-nightshade-300';
}

function getMappedColumn(status: string, mapping: StatusMapping): string | null {
  for (const [col, statuses] of Object.entries(mapping)) {
    if (statuses.includes(status)) return col;
  }
  return null;
}

export function StatusGroupConfig({ sfdcStatuses, scorecardColumns, mapping, onChange, disabled = false }: StatusGroupConfigProps) {
  const toggle = (status: string, column: string) => {
    const next = { ...mapping };
    // Remove from all columns first
    for (const col of scorecardColumns) {
      next[col] = (next[col] ?? []).filter(s => s !== status);
    }
    // Add to target column if it wasn't already there
    const wasAlreadyThere = getMappedColumn(status, mapping) === column;
    if (!wasAlreadyThere) {
      next[column] = [...(next[column] ?? []), status];
    }
    onChange(next);
  };

  return (
    <div>
      <h2 className="text-[11px] font-mono uppercase tracking-wider font-medium text-nightshade-900 dark:text-nightshade-100 mb-1">
        Bucket mapping
      </h2>
      <p className="text-xs text-nightshade-500 dark:text-nightshade-400 mb-4">
        Map each SFDC member status to a bucket. Unmapped statuses are excluded from the waterfall.
      </p>

      <div className="bg-white dark:bg-nightshade-900 ring-1 ring-nightshade-200 dark:ring-nightshade-700 rounded-2xl overflow-hidden">
        {/* Header row */}
        <div className="grid border-b border-nightshade-100 dark:border-nightshade-800 bg-nightshade-50 dark:bg-nightshade-800/50 px-4 py-2.5"
          style={{ gridTemplateColumns: `1fr auto 1fr` }}>
          <span className="text-[11px] font-mono uppercase tracking-wider font-medium text-nightshade-500">SFDC Status</span>
          <span />
          <span className="text-[11px] font-mono uppercase tracking-wider font-medium text-nightshade-500">Maps to bucket</span>
        </div>

        {/* Status rows */}
        {sfdcStatuses.map((status, i) => {
          const mappedCol = getMappedColumn(status, mapping);
          return (
            <div
              key={status}
              className={`grid items-center px-4 py-3 gap-4 ${i < sfdcStatuses.length - 1 ? 'border-b border-nightshade-100 dark:border-nightshade-800' : ''}`}
              style={{ gridTemplateColumns: `1fr auto 1fr` }}
            >
              {/* SFDC status chip */}
              <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold w-fit ${statusChipClass(status)}`}>
                {status}
              </span>

              <ArrowRight className="w-3.5 h-3.5 text-nightshade-300 dark:text-nightshade-600" />

              {/* Column buttons */}
              <div className="flex items-center gap-2 flex-wrap">
                {scorecardColumns.map(col => (
                  <button
                    key={col}
                    onClick={() => !disabled && toggle(status, col)}
                    disabled={disabled}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-all duration-150 ${
                      mappedCol === col
                        ? 'bg-blueberry-500 text-white shadow-sm'
                        : 'bg-nightshade-100 dark:bg-nightshade-800 text-nightshade-500 dark:text-nightshade-400 hover:bg-nightshade-200 dark:hover:bg-nightshade-700'
                    } ${disabled ? 'cursor-default' : ''}`}
                  >
                    {col}
                  </button>
                ))}
                <button
                  onClick={() => {
                    if (disabled || mappedCol === null) return;
                    // Remove from all columns (unmap → excluded)
                    const next = { ...mapping };
                    for (const col of scorecardColumns) {
                      next[col] = (next[col] ?? []).filter(s => s !== status);
                    }
                    onChange(next);
                  }}
                  disabled={disabled}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-all duration-150 ${
                    mappedCol === null
                      ? 'bg-pomegranate-500 text-white shadow-sm'
                      : 'bg-pomegranate-50 dark:bg-pomegranate-950/30 text-pomegranate-500 dark:text-pomegranate-400 hover:bg-pomegranate-100 dark:hover:bg-pomegranate-900'
                  } ${disabled ? 'cursor-default' : ''}`}
                >
                  Excluded
                </button>
              </div>
            </div>
          );
        })}
      </div>

    </div>
  );
}
